// src/pages/dashboard/WalletDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { walletService } from "../../services/api/wallet.service"
import { useBitcoinFees, useEthereumFees, useBnbFees, useTronFees, useSolanaFees } from '../../hooks/hooks';
import { 
  QrCodeIcon, 
  EyeIcon, 
  ExchangeIcon, 
  ArrowLeftIcon, 
  CopyIcon 
} from '../../components/ui/Icons';
import WalletDetailLayout from './wallet-detail/shared/WalletDetailLayout';
import WalletQRModal from './wallet-detail/shared/WalletQRModal';
import WalletPrivateKeyModal from './wallet-detail/shared/WalletPrivateKeyModal';
import WalletMnemonicModal from './wallet-detail/shared/WalletMnemonicModal';
// import TransactionsList from './wallet-detail/shared/TransactionsList';
// import { formatDate } from './wallet-detail/shared/wallet-utils';
import '../../assets/styles/wallet-detail-button.css'; // Importamos los estilos de botones
import type { AppDispatch, RootState } from '../../store';
interface WalletType {
    id:string,
    userId:string,
    address:string,
    createdAt: string,
    balances:{
      btc?:string;
      eth?:string;
      usdt?:string;
      usdc?:string;
      trx?:string;
      bnb?:string;
      sol?:string;
    }
}
export default function WalletDetail() {

  const { token, address } = useParams<{ token: string; address: string }>();
  const { data:{mainWallets, cryptoWallets}, status } = useSelector((state: RootState) => state.assets);
  const navigate = useNavigate();
  const [showQRCode, setShowQRCode] = useState<boolean>(false);
  const [qrCodeData, setQRCodeData] = useState<string | null>(null);
  const [showPrivateKey, setShowPrivateKey] = useState<boolean>(false);
  const [privateKeyData, setPrivateKeyData] = useState<string | null>(null);
  const [showMnemonic, setShowMnemonic] = useState<boolean>(false);
  const [mnemonicData, setMnemonicData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [network, setNetwork] = useState<string>("");
  const [gasFees, setGasFees] = useState<any>(null);
  const [gasFeesLoading, setGasFeesLoading] = useState<boolean>(false);
  const [wallet, setWallet] = useState<WalletType>({
    id:"",
    userId:"",
    address:"",
    createdAt: "",
    balances:{}
  });
  const [balance, setBalance] = useState<string>('')

  // Hooks para obtener gas fees según el blockchain
  const ethereumFees = useEthereumFees(
    token === 'eth' || token === 'usdt_erc20' || token === 'usdc_erc20' ? token : ''
  );
  const bitcoinFees = useBitcoinFees(token === 'btc' ? 'BTC' : '');
  const bnbFees = useBnbFees(
    token === 'bnb' || token === 'usdt_bep20' ? token : ''
  );
  const tronFees = useTronFees(
    token === 'trx' || token === 'usdt_tron' ? token : '',
    wallet?.address || ''
  );
  const solanaFees = useSolanaFees(
    token === 'sol' || token === 'usdt_spl' || token === 'usdc_spl' ? token : ''
  );

  // Efecto para actualizar los gas fees según el blockchain
  useEffect(() => {
    let fees = null;
    let loading = false;

    switch(network) {
      case 'ethereum':
        fees = ethereumFees.data;
        loading = ethereumFees.isLoading;
        break;
      case 'bitcoin':
        fees = bitcoinFees.data;
        loading = bitcoinFees.isLoading;
        break;
      case 'bnb':
        fees = bnbFees.data;
        loading = bnbFees.isLoading;
        break;
      case 'tron':
        fees = tronFees.data;
        loading = tronFees.isLoading;
        break;
      case 'solana':
        fees = solanaFees.data;
        loading = solanaFees.isLoading;
        break;
    }

    setGasFees(fees);
    setGasFeesLoading(loading);
  }, [network, ethereumFees.data, bitcoinFees.data, bnbFees.data, tronFees.data, solanaFees.data,
      ethereumFees.isLoading, bitcoinFees.isLoading, bnbFees.isLoading, tronFees.isLoading, solanaFees.isLoading]);

  const handleRefreshGasFees = () => {
    switch(network) {
      case 'ethereum':
        ethereumFees.refetch();
        break;
      case 'bitcoin':
        bitcoinFees.refetch();
        break;
      case 'bnb':
        bnbFees.refetch();
        break;
      case 'tron':
        tronFees.refetch();
        break;
      case 'solana':
        solanaFees.refetch();
        break;
    }
  };

  const handleShowQRCode = async () => {
      try {
        setIsLoading(true);      
        const endpoint = `/crypto-web3/${network}/wallet/${address}/qr`;
        const res = await walletService.getQRCode(endpoint, address);  
        setQRCodeData(res);
        setShowQRCode(true);
        setIsLoading(false);
      } catch (error) {
        toast.error('Failed to load QR code');
        setIsLoading(false);
        
        // En caso de error, usar un generador público
        const fallbackQR = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${address}`;
        setQRCodeData(fallbackQR);
        setShowQRCode(true);
      }
  };
  const handleShowPrivateKey = async () => {
    try {
      setIsLoading(true);
      const endpoint = `/crypto-web3/${network}/wallet/${address}/private-key`;
      const res = await walletService.getPrivateKey(endpoint, address); 
      const data = await res.json();
      setPrivateKeyData(data.data.privateKey);   
      setShowPrivateKey(true);
      setIsLoading(false);
    } catch (error) {
      toast.error('Failed to load private key');
      setPrivateKeyData('Unable to retrieve key at this time. Please try again later.');
      setShowPrivateKey(true);
      setIsLoading(false);
    }
  };  
  const handleShowMnemonic = async () => {
      try {
        setIsLoading(true);     
        const endpoint = `/crypto-web3/${network}/wallet/${address}/mnemonic`;
        const res = await walletService.getSeed(endpoint, address); 
        const data = await res.json();
        setMnemonicData(data.data.mnemonic);      
        setShowMnemonic(true);
        setIsLoading(false);
      } catch (error) {
        toast.error('Failed to load mnemonic phrase');
        setMnemonicData('Unable to retrieve seed phrase at this time. Please try again later.');
        setShowMnemonic(true);
        setIsLoading(false);
      }
  };
  const walletActions = (
    <>
      <button 
        onClick={handleShowQRCode}
        className="wallet-btn wallet-btn-sm sm:wallet-btn-default w-full lsm:w-auto max-w-36"
        disabled={isLoading}
      >
        <QrCodeIcon style={{ marginRight: '5px' }} /> 
        Show QR Code
      </button>
      
      <button 
        onClick={handleShowPrivateKey}
        className="wallet-btn wallet-btn-sm sm:wallet-btn-default w-full lsm:w-auto max-w-36"
        disabled={isLoading}
      >
        <EyeIcon style={{ marginRight: '5px' }} /> 
        Private Key
      </button>
      {!["tron", "bitcoin"].includes(network) && (
        <button 
          onClick={handleShowMnemonic}
          className="wallet-btn wallet-btn-sm sm:wallet-btn-default w-full lsm:w-auto max-w-36"
          disabled={isLoading}
        >
          <EyeIcon style={{ marginRight: '5px' }} /> 
          Seed Phrase
        </button>
      ) }
      
      <button 
        // onClick={handleCopyAddress}
        className="wallet-btn wallet-btn-sm sm:wallet-btn-default w-full lsm:w-auto max-w-36"
      >
        <CopyIcon style={{ marginRight: '5px' }} /> 
        Copy Address
      </button>
      
      <button
        onClick={() => navigate(`/dashboard/transfers?wallet=${address}&currency=${token.toUpperCase()}&showWarning=true`)}
        className="wallet-btn wallet-btn-sm sm:wallet-btn-default w-full lsm:w-auto max-w-36"
      >
        <ExchangeIcon style={{ marginRight: '5px' }} /> 
        Transfer
      </button>
    </>
  );

  useEffect(() => {
    if (network) {
      setWallet(cryptoWallets.data[network])
    }
  }, [network, cryptoWallets]);
  useEffect(() =>{
    if(wallet){
      console.log(wallet[0]?.balances[token])
      setBalance(wallet[0]?.balances[token])
    }
  }, [wallet])
  useEffect(() => {
    switch (token) {
      case "btc":
        setNetwork("bitcoin");
        break;
      case "eth":
         setNetwork("ethereum");
        break;
      case "usdt_erc20":
         setNetwork("ethereum");
        break;
      case "usdc_erc20":
        setNetwork("ethereum");
        break;
      case "bnb":
          setNetwork("bnb");
        break;
      case "usdt_bep20":
        setNetwork("bnb");
        break;
      case "trx":
         setNetwork("tron");
        break;
      case "usdt_trc20":
        setNetwork("tron");
        break;
      case "sol":
         setNetwork("solana");
        break;
      case "usdt_spl":
         setNetwork("solana");
        break;
      case "usdc_spl":
        setNetwork("solana");
        break;
      default:
        setNetwork(token);
    }
  }, [token]);
   return (
    // <></>
     <WalletDetailLayout
       token={token}
       address={address}
       balance={balance}
       isLoading={false}
       // onRefresh={handleForceRefresh}
       actions={walletActions}
     >


       {/* Historial de transacciones */}
       {/* <TransactionsList
         transactions={displayData?.transactions || []}
         currency="ETH"
         address={address}
       /> */}
       
       {/* Modal para mostrar QR Code */}
       {showQRCode && (
         <WalletQRModal
           address={address}
           qrCodeData={qrCodeData}
           currency={token?.toUpperCase()}
           network={network}
           onClose={() => setShowQRCode(false)}
         />
       )}
       
       {/* Modal para mostrar Private Key */}
       {showPrivateKey && (
         <WalletPrivateKeyModal
           privateKeyData={privateKeyData}
           onClose={() => setShowPrivateKey(false)}
         />
       )}
       
       {/* Modal para mostrar Mnemonic */}
       {showMnemonic && (
         <WalletMnemonicModal
           mnemonicData={mnemonicData}
           onClose={() => setShowMnemonic(false)}
         />
       )}
     </WalletDetailLayout>
   );
}